import { useMemo, useState } from 'react';
import { useFetchRevenueReportQuery, useFetchRevenueCustomersQuery } from '../../api';
import { formatAmount } from '../../utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Typography } from '@mui/material';
import moment from 'moment';

const RevenueReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [view, setView] = useState<'payments' | 'customers'>('payments');

  const params = useMemo(
    () => ({
      startDate: startDate ? startDate.format('YYYY-MM-DD') : dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: endDate ? endDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    }),
    [startDate, endDate]
  );

  const { data: revenue, isLoading: revenueLoading, isFetching: revenueFetching } = useFetchRevenueReportQuery(params);
  const { data: customers, isLoading: customersLoading, isFetching: customersFetching } = useFetchRevenueCustomersQuery(params);

  const isLoading = revenueLoading || revenueFetching || customersLoading || customersFetching;

  const items: any[] = revenue?.items || [];
  const customerRows: any[] = customers?.customers || [];

  const membershipShare = (revenue?.totalRevenue || 0) > 0 ? ((revenue?.membershipRevenue || 0) / revenue.totalRevenue) * 100 : 0;
  const rentalShare = (revenue?.totalRevenue || 0) > 0 ? ((revenue?.rentalRevenue || 0) / revenue.totalRevenue) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Typography variant="h6" className="font-bold text-slate-800">Revenue Report</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex flex-wrap items-center gap-3">
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small' } }} />
          </div>
        </LocalizationProvider>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px] text-gray-500 italic">Loading revenue…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryTile label="Total Revenue" value={`RWF ${formatAmount(revenue?.totalRevenue || 0)}`} accent="emerald" />
            <SummaryTile label="Membership Revenue" value={`RWF ${formatAmount(revenue?.membershipRevenue || 0)}`} accent="blue" />
            <SummaryTile label="Rental Revenue" value={`RWF ${formatAmount(revenue?.rentalRevenue || 0)}`} accent="purple" />
            <SummaryTile label="Payments" value={(revenue?.count || 0).toLocaleString()} />
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-800 text-sm mb-4">Revenue Breakdown</h3>
            <div className="space-y-4">
              <SplitBar label="Membership" value={revenue?.membershipRevenue || 0} pct={membershipShare} color="bg-blue-500" />
              <SplitBar label="Rental" value={revenue?.rentalRevenue || 0} pct={rentalShare} color="bg-purple-500" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex border-b border-slate-200">
              <TabButton active={view === 'payments'} onClick={() => setView('payments')}>
                Payments ({items.length})
              </TabButton>
              <TabButton active={view === 'customers'} onClick={() => setView('customers')}>
                Customers ({customers?.customerCount || 0})
              </TabButton>
            </div>

            {view === 'payments' ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Name</th>
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Type</th>
                      <th className="py-3 px-4 text-right font-bold text-slate-700">Amount Paid</th>
                      <th className="py-3 px-4 text-right font-bold text-slate-700">Remaining</th>
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Channel</th>
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Reference</th>
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Date</th>
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr><td colSpan={8} className="py-8 text-center text-slate-400">No payments in this period</td></tr>
                    ) : items.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-slate-800 font-medium">{p.paymentName || '-'}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${p.paymentType === 'MEMBERSHIP' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                            {p.paymentType}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-slate-900">RWF {formatAmount(p.paymentAmount || 0)}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{p.remainingAmount > 0 ? `RWF ${formatAmount(p.remainingAmount)}` : '-'}</td>
                        <td className="py-3 px-4 text-slate-700">{p.channel || '-'}</td>
                        <td className="py-3 px-4 text-slate-600 text-xs">{p.reference || '-'}</td>
                        <td className="py-3 px-4 text-slate-600 text-xs">{moment(p.paymentDate || p.createAt).format('MMM D, YYYY')}</td>
                        <td className="py-3 px-4">
                          <span className={`status ${(p.status || '').toLowerCase()}`}>{p.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Customer</th>
                      <th className="py-3 px-4 text-left font-bold text-slate-700">Contact</th>
                      <th className="py-3 px-4 text-center font-bold text-slate-700">Payments</th>
                      <th className="py-3 px-4 text-right font-bold text-slate-700">Membership</th>
                      <th className="py-3 px-4 text-right font-bold text-slate-700">Rental</th>
                      <th className="py-3 px-4 text-right font-bold text-slate-700">Total Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerRows.length === 0 ? (
                      <tr><td colSpan={6} className="py-8 text-center text-slate-400">No customer revenue in this period</td></tr>
                    ) : customerRows.map((c) => (
                      <tr key={c.userId} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-slate-800 font-medium">
                          <div>{c.fullName || '-'}</div>
                          {c.companyName && <div className="text-xs text-slate-500 mt-0.5">{c.companyName}</div>}
                        </td>
                        <td className="py-3 px-4 text-slate-600 text-xs">
                          <div>{c.email || '-'}</div>
                          <div>{c.phoneNumber || '-'}</div>
                        </td>
                        <td className="py-3 px-4 text-center text-slate-700">{c.paymentCount}</td>
                        <td className="py-3 px-4 text-right text-slate-700">RWF {formatAmount(c.membershipPaid || 0)}</td>
                        <td className="py-3 px-4 text-right text-slate-700">RWF {formatAmount(c.rentalPaid || 0)}</td>
                        <td className="py-3 px-4 text-right font-bold text-emerald-600">RWF {formatAmount(c.totalPaid || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent }: { label: string; value: string; accent?: 'emerald' | 'blue' | 'purple' }) => {
  const color = accent === 'emerald' ? 'text-emerald-600' : accent === 'blue' ? 'text-blue-600' : accent === 'purple' ? 'text-purple-600' : 'text-slate-800';
  return (
    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
};

const SplitBar = ({ label, value, pct, color }: { label: string; value: number; pct: number; color: string }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <span className="text-sm font-bold text-slate-900">RWF {formatAmount(value)} ({pct.toFixed(1)}%)</span>
    </div>
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

const TabButton = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    onClick={onClick}
    className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
      active ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

export default RevenueReport;
